import { Box, Stack } from '@mui/material';
import { useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { iconSizeByButtonSize } from '@/components/form/baseInputs/Button/styles';
import { Checkbox } from '@/components/form/baseInputs/Checkbox';
import { Edit02Icon } from '@/components/icons/Edit02Icon';
import { EyeIcon } from '@/components/icons/EyeIcon';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { ClientProfileSideDrawer } from '@/modules/clientProfiles/ClientProfileForm/ClientProfileModal';
import { useFamilyTreeContext } from '@/modules/familyTree/contexts';
import { useFamilyTreeRelationships } from '@/modules/familyTree/hooks/useFamilyTreeRelationships';

interface SidePanelFooterProps {
  householdId: string;
  /**
   * Hide the "Edit details" button entirely (and its fallback drawer). Used on
   * the presentation, where editing a person's details is offered from the tile
   * toolbar instead of this sidebar.
   */
  hideEditDetailsButton?: boolean;
}

export function SidePanelFooter({
  householdId,
  hideEditDetailsButton = false,
}: SidePanelFooterProps) {
  const hasEditPermission = useCanEditHouseholdData();

  const { selectedPerson } = useFamilyTreeRelationships();
  const { state, dispatch } = useFamilyTreeContext();

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Get current node config or use defaults
  const nodeConfig = selectedPerson
    ? (state.nodeConfigs[selectedPerson.id] ?? {
        showNotes: false,
        showTile: true,
      })
    : { showNotes: false, showTile: true };

  const handleUpdateNodeConfig = (
    key: 'showNotes' | 'showTile',
    value: boolean
  ) => {
    if (selectedPerson) {
      dispatch({
        type: 'UPDATE_NODE_CONFIG',
        personId: selectedPerson.id,
        key,
        value,
      });
    }
  };

  return (
    <>
      <Stack
        direction="row"
        width="100%"
        alignItems="center"
        justifyContent="space-between"
      >
        {hasEditPermission && selectedPerson && !hideEditDetailsButton && (
          <Button
            variant="transparent"
            size="sm"
            startIcon={Edit02Icon}
            onClick={() => setIsModalOpen(true)}
          >
            Edit {selectedPerson.displayName}
          </Button>
        )}
        {/* When the edit button is hidden (presentation), this spacer keeps
            "View options" pinned right. On-site, space-between handles the
            layout — including the unlinked panel, where it stays at the start. */}
        {hideEditDetailsButton && <Box flexGrow={1} />}
        <ButtonWithPopover
          label="View options"
          buttonSx={{
            whiteSpace: 'nowrap',
          }}
          size="sm"
          variant="secondary"
          popperVariant="menuAbove"
          startIcon={EyeIcon}
          iconProps={{
            size: iconSizeByButtonSize.sm,
          }}
        >
          <MenuItem
            icon={
              <Checkbox
                value={nodeConfig.showTile}
                onChange={(e) =>
                  handleUpdateNodeConfig('showTile', e.target.checked)
                }
              />
            }
            label="Show individual on tree"
            onClick={(e) => e.stopPropagation()}
          />
          <MenuItem
            icon={
              <Checkbox
                value={nodeConfig.showNotes}
                onChange={(e) =>
                  handleUpdateNodeConfig('showNotes', e.target.checked)
                }
              />
            }
            label="Show notes for individual"
            onClick={(e) => e.stopPropagation()}
          />
        </ButtonWithPopover>
      </Stack>
      {/* Skip the profile drawer when the edit button is hidden entirely. */}
      {!hideEditDetailsButton && selectedPerson && (
        <ClientProfileSideDrawer
          clientProfileId={selectedPerson.id}
          householdId={householdId}
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          clientProfileTypeDisplay="individual"
        />
      )}
    </>
  );
}
